import type { ArcadeLayoutId, ArcadeTheme } from './themes'
import type { BoardModel, BoardPlayer, RivalryFixture, RivalryModel } from './viewModel'
import { buildRivalry } from './viewModel'
import { Mover, PixelAvatar, Sparkline, SurfaceBreakdown, SurfaceSplitNote } from './atoms'

/**
 * Layout SKELETONS: the page structures a theme can render in.
 *
 * The contract every skeleton signs, and the reason structural variants are
 * safe at all:
 *
 *   A skeleton receives a `BoardModel` and nothing else. `BoardModel` has no
 *   `spend` field and no raw `move` (see `arcade/viewModel.ts`), so a skeleton
 *   cannot render a cost figure — not because it is careful, but because the
 *   number is not reachable from the type it is given. The rank move comes
 *   through as a `Mover` union whose withheld arm carries no delta, so under
 *   hide-costs a skeleton cannot draw an arrow either: it has been handed a
 *   value with nothing in it. Every skeleton renders the move through the one
 *   shared `<Mover>` atom, which returns null for that arm. A skeleton also
 *   cannot fetch: the page owns the single `/api/leaderboard` call.
 *
 * Each skeleton paints exclusively through the `--a-*` custom properties that
 * `arcadeCss` emits, so a new skeleton can never introduce a colour a theme
 * has not defined, and it declares a `data-rank-list` marker so the structural
 * test can prove the tree it asserted against is the tree that rendered.
 *
 * Each skeleton's `data-rank-list` marker equals its `ArcadeLayoutId`, so the
 * structural test asserts one thing rather than maintaining a mapping.
 *
 * Seven skeletons serve the ten themes: `board` (the high-score table, with
 * an optional ticker slot, used by Neon Cabinet, District Map, Wrapped and
 * Trading Floor), `bento` (everything at once — the default), `ladder`
 * (divisions and a promotion cut-line), `lanes` (rank as distance), `raid`
 * (a shared health bar), `flaps` (the departures board, and the reception
 * screen) and `motd` (the terminal scrollback).
 */

export interface SkeletonProps {
  model: BoardModel
  theme: ArcadeTheme
  onSelectPlayer: (id: string) => void
  /**
   * Draw this skeleton's own row of org-wide tiles. Default true, and the ONE
   * caller that passes false is Big Spenders' summary tab, where
   * `SpendersPanels` has already put a richer eleven-tile row on screen.
   *
   * WHY A PROP AND NOT A DELETION. Every other view renders a skeleton with
   * nothing above it, and the strip is the only place those numbers appear at
   * all — removing it would fix a duplicate on one tab by leaving five other
   * tabs with no org figures. The duplication is a composition problem, so it
   * is settled where the composition happens.
   */
  orgTiles?: boolean
}

/** Keyboard activation for the div-based row/plinth controls. */
function activate(fn: () => void) {
  return (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      fn()
    }
  }
}

/** One compact award card in the right rail. Reads only derived fields —
 *  `cost` is already a `Figure`, so the card prints `cost.text` and never
 *  formats a currency value itself. */
function AwardCard({
  title,
  accent,
  player,
  label,
}: {
  title: string
  accent: string
  player: BoardPlayer
  label: string
}) {
  return (
    <div className="acard" style={{ ['--ac' as string]: accent }}>
      <div className="atitle">{title}</div>
      <div className="aname">{player.first}</div>
      <div className="alabel">{label}</div>
      <div className="astats">
        <div className="astat">
          LVL<b>{player.level}</b>
        </div>
        {/* The cost slot is the one place the withheld placeholder still
            shows on the board skeleton: dropping it entirely would remove the
            "there is a number here you cannot see" affordance, which is a
            different thing from removing the number. */}
        <div className="astat">
          SPEND<b data-withheld={player.cost.withheld ? '1' : '0'}>{player.cost.text}</b>
        </div>
        <div className="astat">
          MAIN<b>{player.cls.name}</b>
        </div>
      </div>
    </div>
  )
}

/** One board row. Shared by the table skeleton; the mobile shell restyles it
 *  to two lines in CSS rather than rendering a second markup variant, so there
 *  is no phone-only tree that can drift from the desktop one. */
function BoardRow({ p, onSelect }: { p: BoardPlayer; onSelect: () => void }) {
  return (
    <div
      className={`row clickable${p.you ? ' you' : ''}`}
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={activate(onSelect)}
    >
      {/* `top3`, not `top`: `.arcade .top` is the page header's selector and
          matches ANY descendant carrying that class, so a rank cell called
          `top` inherited the header's rules. Harmless-looking on desktop,
          visible on a phone as a grey sticky-header box behind the rank. */}
      <div className={`rank${p.rank <= 3 ? ' top3' : ''}`}>
        {String(p.rank).padStart(2, '0')}
        <Mover mover={p.mover} />
      </div>
      <div className="who">
        <PixelAvatar name={p.name} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="nm">{p.name}</div>
          {p.badges.length > 0 || p.streak >= 3 ? (
            <div className="badges">
              {p.streak >= 3 ? <span className="bdg flame">🔥{p.streak}D</span> : null}
              {p.badges.map((b) => (
                <span key={b.label} className={`bdg ${b.kind}`}>
                  {b.label}
                </span>
              ))}
            </div>
          ) : null}
          <Sparkline data={p.spark} />
        </div>
      </div>
      <div className="clsC">
        {p.models} model{p.models === 1 ? '' : 's'}
        <span className="role">mains {p.cls.name}</span>
      </div>
      <div className={`sc${p.score.withheld ? '' : ' tok'}`} data-withheld={p.score.withheld ? '1' : '0'}>
        {p.score.text}
        {/* The total stays the headline and still ranks the row. This sits
            UNDER it saying what it is made of — chat and Claude Code are
            different behaviours that were collapsing into one number. It is
            not an addition: the two halves ARE the figure above them, so the
            atom prints no `+` and the row shows no third amount. Renders
            nothing at all when the split is unknown (see `SurfaceBreakdown`),
            which is why there is no `0` to misread here. */}
        <SurfaceBreakdown surface={p.surface} />
      </div>
      <div className="lvl">
        <div className="lab">LVL {p.level}</div>
        <div className="track">
          <div className="fill" style={{ width: `${(p.level / 99) * 100}%` }} />
        </div>
      </div>
    </div>
  )
}

/**
 * `board` — the high-score table. Podium, ranked rows, award rail.
 *
 * This is the layout that shipped in v0.14.0, unchanged in shape so that the
 * seam refactor is provable against a tree people already know. The differences
 * are all subtractions: the wooden-spoon award card is gone. The mover badge
 * under the rank returned in v0.18.x, now sourced from the `Mover` union.
 */
export function BoardSkeleton({ model, theme, onSelectPlayer }: SkeletonProps) {
  const podiumColors = [theme.gold, theme.silver, theme.bronze]
  return (
    <div data-rank-list="board">
      {theme.ticker ? <Ticker model={model} /> : null}
      <div className="podium">
        {model.podium.map((p, i) => (
          <div
            className="plinth clickable"
            key={p.id}
            role="button"
            tabIndex={0}
            /* `--ord` not `order`: the desktop 2/1/3 arrangement is a
               presentation choice that must not survive the single-column
               collapse, and a custom property can be overridden by the mobile
               media query where an inline `order` could not. That inline value
               is why every phone showed the runner-up above the champion. */
            style={{ ['--ord' as string]: i === 0 ? 2 : i === 1 ? 1 : 3, ['--pos' as string]: podiumColors[i] }}
            onClick={() => onSelectPlayer(p.id)}
            onKeyDown={activate(() => onSelectPlayer(p.id))}
          >
            <div className="medal">{['1ST', '2ND', '3RD'][i]}</div>
            <PixelAvatar name={p.name} size={i === 0 ? 72 : 60} className={`podav podav-${i + 1}`} />
            <div className="who">{p.first}</div>
            <div className="cls">{p.cls.role}</div>
            <div className="score">{p.score.text}</div>
          </div>
        ))}
      </div>

      <div className="layout">
        <div>
          <div className="board">
            <div className="row head">
              <div>RANK</div>
              <div>PLAYER</div>
              <div className="hc">ARSENAL</div>
              <div style={{ textAlign: 'right' }}>{model.mode === 'spend' ? 'SPEND' : 'TOKENS'}</div>
              <div className="hl">LEVEL</div>
            </div>
            {model.ranked.map((p) => (
              <BoardRow key={p.id} p={p} onSelect={() => onSelectPlayer(p.id)} />
            ))}
          </div>
          {/* One sentence for the whole board when the breakdown could not be
              computed, rather than a blank slot on every row that a reader
              would fill in for themselves. */}
          <SurfaceSplitNote shown={model.surfaceSplit} />
        </div>

        {model.leader ? (
          <aside className="awards">
            <AwardCard
              title="👑 TOP PLAYER"
              accent={theme.gold}
              player={model.leader}
              label={`RANK: ${theme.rankTitles.top}`}
            />
            {model.risingStar && model.risingStar.id !== model.leader.id ? (
              <AwardCard
                title="🌟 RISING STAR"
                accent={theme.cyan}
                player={model.risingStar}
                label="CLIMBING THE RANKS"
              />
            ) : null}
            {/* There is deliberately no third card. The WOODEN SPOON award was
                removed in v0.15.0: this board is named, company-wide and linked
                to every staff member, and the design set's own constraint
                forbids calling out the bottom. */}
          </aside>
        ) : null}
      </div>
    </div>
  )
}

/**
 * The exchange ticker. A SLOT on the board skeleton, switched on by
 * `theme.ticker`, not a skeleton of its own.
 *
 * Trading Floor's quote board minus its daily-change column is the ordinary
 * table, and that column was a spend-rank delta — prohibited. So the ticker
 * carries what the shared payload legitimately has: topic volumes, team totals
 * and streak counts. Never a price and never a movement.
 *
 * The tape is duplicated so the CSS translate can loop seamlessly; the copy is
 * `aria-hidden` so a screen reader hears each item once.
 */
function Ticker({ model }: { model: BoardModel }) {
  const items = [
    ...model.topics.slice(0, 4).map((t) => ({ k: t.topic.toUpperCase().replace(/\s+/g, '-'), v: t.count.toLocaleString() })),
    ...model.teams.slice(0, 3).map((t) => ({ k: `${t.dept.toUpperCase().replace(/\s+/g, '-')}.TEAM`, v: t.text })),
    { k: 'STREAKS LIVE', v: String(model.org.liveStreaks) },
    { k: 'NEW THIS WINDOW', v: String(model.org.newThisWindow) },
  ]
  if (items.length === 0) return null
  const tape = (hidden: boolean) => (
    <div className="tape" aria-hidden={hidden || undefined}>
      {items.map((it) => (
        <span className="tick" key={`${hidden ? 'b' : 'a'}-${it.k}`}>
          {it.k} <b>{it.v}</b>
        </span>
      ))}
    </div>
  )
  return (
    <div className="tickerbar" data-slot="ticker">
      <div className="tapes">
        {tape(false)}
        {tape(true)}
      </div>
    </div>
  )
}

/**
 * `ladder` — divisions with a promotion cut-line.
 *
 * The same ranked array, partitioned. Division names come from
 * `theme.rankTitles`, which already exists, so the ladder reads in each
 * theme's own nomenclature rather than a hardcoded Gold/Silver.
 *
 * The bottom band is a DEVELOPMENT LEAGUE and it is unranked: everyone in it
 * is listed, but without a position, because the point of this skeleton is
 * that the low end is never called out by rank. That is also why it lists
 * names at all rather than showing a count — invisible is not the same as
 * unranked, and the completeness test holds every layout to showing every
 * player exactly once.
 */
export function LadderSkeleton({ model, theme, onSelectPlayer, orgTiles = true }: SkeletonProps) {
  const n = model.ranked.length
  // Percentile cuts over the same array the board ranks. Small boards collapse
  // gracefully: with three players there is one in each band.
  const firstCut = Math.max(1, Math.ceil(n * 0.25))
  const secondCut = Math.max(firstCut, Math.ceil(n * 0.6))
  const divisions = [
    { key: 'top', title: theme.rankTitles.elite, players: model.ranked.slice(0, firstCut), tone: 'gold' },
    { key: 'mid', title: theme.rankTitles.high, players: model.ranked.slice(firstCut, secondCut), tone: 'silver' },
  ].filter((d) => d.players.length > 0)
  const development = model.ranked.slice(secondCut)

  return (
    <div className="ladder" data-rank-list="ladder">
      <div className="divs">
        {divisions.map((d, i) => (
          <div className={`div ${d.tone}`} key={d.key}>
            <h4>
              <span className="bd" />
              {d.title}
              <span className="dcount">{d.players.length}</span>
            </h4>
            {d.players.map((p) => (
              <div
                className={`lrow clickable${p.you ? ' you' : ''}`}
                key={p.id}
                role="button"
                tabIndex={0}
                onClick={() => onSelectPlayer(p.id)}
                onKeyDown={activate(() => onSelectPlayer(p.id))}
              >
                <span className="rk">
                  {p.rank}
                  <Mover mover={p.mover} />
                </span>
                <PixelAvatar name={p.name} size={28} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="nm">{p.name}</div>
                  <div className="sub">
                    {p.department}
                    {p.streak >= 3 ? ` · ${p.streak}-day streak` : ''}
                  </div>
                </div>
                <span className="pts" data-withheld={p.score.withheld ? '1' : '0'}>
                  {p.score.text}
                  {/* Was `+ $12.00 key` — the developer-key dollars, printed
                      with a plus in front of a total that ALREADY CONTAINED
                      THEM. Same amount, same window, invited a reader to add
                      it on twice. It is a breakdown of the figure above, so it
                      now renders as one, through the atom every skeleton
                      shares. */}
                  <SurfaceBreakdown surface={p.surface} />
                </span>
              </div>
            ))}
            {i === 0 && divisions.length > 1 ? (
              /* NOT an arrow glyph. `▲`/`▼` are reserved for mover deltas
                 across the whole arcade — a reader cannot tell a promotion
                 marker from a "moved up 2 places" marker, and the dashed rule
                 already says "line". That reservation is why movers always
                 carry their magnitude and why this cut-line uses `◆`: the two
                 have to stay tellable apart now that both are on screen
                 together whenever hide-costs is off. */
              <div className="cut">◆ PROMOTION LINE — the top of {divisions[1]!.title} comes up next window</div>
            ) : null}
          </div>
        ))}
        {/* Under the divisions, not above them: it is a caveat about one
            column of the rows, and leading with it would read as a caveat
            about the standings themselves — which did not change. */}
        <SurfaceSplitNote shown={model.surfaceSplit} />
      </div>

      <aside className="ladderside">
        <div className="lcard">
          <div className="k">{theme.rankTitles.base}</div>
          {development.length === 0 ? (
            <p className="hint">Everyone is in a division this window.</p>
          ) : (
            <>
              <p className="devnote">
                {development.length} building momentum. Everyone who logs a chat moves up — no bottom call-out.
              </p>
              <div className="devlist">
                {development.map((p) => (
                  <button
                    type="button"
                    className={`chip${p.you ? ' you' : ''}`}
                    key={p.id}
                    onClick={() => onSelectPlayer(p.id)}
                  >
                    <PixelAvatar name={p.name} size={20} />
                    <span className="cname">{p.name}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        {!orgTiles ? null : (
        <div className="lcard">
          <div className="k">This window</div>
          <div className="lstat">
            <span>Players</span>
            <b>{model.org.players}</b>
          </div>
          <div className="lstat">
            <span>Live streaks</span>
            <b>{model.org.liveStreaks}</b>
          </div>
          <div className="lstat">
            <span>New arrivals</span>
            <b>{model.org.newThisWindow}</b>
          </div>
        </div>
        )}
      </aside>
    </div>
  )
}

/** How many players get their own lane before the rest become a list. A
 *  142-lane track is not a design, it is a stripe pattern. */
const LANE_COUNT = 8

/**
 * `lanes` — rank as distance.
 *
 * Each car sits at `tokens / leaderTokens` along its lane with the flag at
 * 100%, so the board shows the GAP rather than an ordinal, which is the one
 * thing a table cannot say.
 *
 * The deck's "Fastest lap" card was a spend-rank delta and is not built. The
 * pit board carries longest streaks and the team standings instead — both
 * survive hide-costs untouched.
 */
export function LanesSkeleton({ model, onSelectPlayer }: SkeletonProps) {
  const lead = model.ranked[0]?.tokens || 1
  const onTrack = model.ranked.slice(0, LANE_COUNT)
  const rest = model.ranked.slice(LANE_COUNT)
  const longestRuns = [...model.ranked]
    .filter((p) => p.streak > 0)
    .sort((a, b) => b.streak - a.streak)
    .slice(0, 3)

  return (
    <div className="gp" data-rank-list="lanes">
      <div className="track">
        {onTrack.map((p) => (
          <div
            className={`lane clickable${p.you ? ' you' : ''}`}
            key={p.id}
            role="button"
            tabIndex={0}
            aria-label={`${p.name}, position ${p.rank}, ${p.score.text}`}
            onClick={() => onSelectPlayer(p.id)}
            onKeyDown={activate(() => onSelectPlayer(p.id))}
          >
            <span className="pos">
              P{p.rank}
              <Mover mover={p.mover} />
            </span>
            {/* Clamped to a floor so the last car is never flush with the grid
                wall and unreadable. */}
            <div className="car" style={{ ['--x' as string]: `${Math.max(12, (p.tokens / lead) * 100)}%` }}>
              <span className="cn">
                {p.name}
                {p.you ? ' · YOU' : ''} <b data-withheld={p.score.withheld ? '1' : '0'}>{p.score.text}</b>
              </span>
              <span className="no">{p.rank}</span>
            </div>
          </div>
        ))}
      </div>

      {rest.length > 0 ? (
        <div className="grid">
          <div className="k">On the grid</div>
          <div className="gridlist">
            {rest.map((p) => (
              <button
                type="button"
                className={`chip${p.you ? ' you' : ''}`}
                key={p.id}
                onClick={() => onSelectPlayer(p.id)}
              >
                <span className="gpos">P{p.rank}</span>
                <PixelAvatar name={p.name} size={20} />
                <span className="cname">{p.name}</span>
                <span className="ccount" data-withheld={p.score.withheld ? '1' : '0'}>
                  {p.score.text}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="pits">
        <div className="mini">
          <div className="k">🔥 Longest runs</div>
          {longestRuns.length === 0 ? (
            <p className="hint">No live streaks this window.</p>
          ) : (
            longestRuns.map((p) => (
              <div className="fl" key={p.id}>
                <span>{p.first}</span>
                <b>{p.streak}D</b>
              </div>
            ))
          )}
        </div>
        <div className="mini">
          <div className="k">🏁 Constructors</div>
          {model.teams.slice(0, 3).map((t) => (
            <div className="fl" key={t.dept}>
              <span>{t.dept}</span>
              <b>{t.text}</b>
            </div>
          ))}
        </div>
        <div className="mini">
          <div className="k">📋 Pit board</div>
          {model.topics.slice(0, 3).map((t) => (
            <div className="fl" key={t.topic}>
              <span>{t.topic}</span>
              <b>{t.count.toLocaleString()}</b>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * `bento` — everything at once.
 *
 * The structural idea, and the reason this is the default: the five ranking
 * modes stop being a tab bar and become simultaneous panels, so the board
 * answers "who" and "what for" in one glance instead of behind two clicks.
 *
 * Every tile derives from the shared payload. Team standings and topics are
 * already in it; active staff, live streaks and new arrivals are counts over
 * `players[]` computed once at the seam. No tile has an endpoint.
 */
/**
 * One row of the Bento HQ leaderboard: rank + mover, avatar, name (with a
 * `· YOU` mark), department + streak/model-count, and the ranked score with
 * its surface breakdown.
 *
 * EXTRACTED from `BentoSkeleton`'s own `.blist` (23 Sep 2026, round 10) so
 * the staff portal's Home tab can show a person's team and the company board
 * with the identical row -- reused, never re-implemented, the same `.brow`
 * CSS class and the same child atoms, so a change to how a row looks only
 * has one place to happen. `BentoSkeleton` below is now a caller, not a
 * second copy.
 */
export function BentoBoardRow({
  p,
  model,
  onSelectPlayer,
  tokensOnly = false,
}: {
  p: BoardPlayer
  model: Pick<BoardModel, 'mode' | 'window'>
  onSelectPlayer: (id: string) => void
  /** Forces the chat/Claude Code breakdown to tokens only, dropping the
   *  dollar figure `SurfaceBreakdown` would otherwise print beside it --
   *  the staff portal's Home boards (round 11, Maroun: "tokens only"),
   *  never the leaderboard's own rows, which are unaffected by default. */
  tokensOnly?: boolean
}) {
  return (
    <div
      className={`brow clickable${p.you ? ' you' : ''}`}
      role="button"
      tabIndex={0}
      onClick={() => onSelectPlayer(p.id)}
      onKeyDown={activate(() => onSelectPlayer(p.id))}
    >
      <span className={`rk${p.rank <= 3 ? ' top3' : ''}`}>
        {p.rank}
        <Mover mover={p.mover} />
      </span>
      <PixelAvatar name={p.name} size={26} photoId={p.id} window={model.window} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="nm">
          {p.name}
          {p.you ? <span className="youmark"> · YOU</span> : null}
        </div>
        <div className="sub">
          {p.department}
          {p.streak >= 3 ? ` · ${p.streak}-day streak` : p.models >= 4 ? ` · ${p.models} models` : ''}
        </div>
      </div>
      <b className="val" data-withheld={p.score.withheld ? '1' : '0'}>
        {p.score.text}
        {/* Bento is the DEFAULT layout, so this is the row most people
            actually read. Same atom, same rule: a breakdown of the
            figure it sits under, never an addition to it. */}
        <SurfaceBreakdown surface={p.surface} tokensOnly={tokensOnly} />
      </b>
    </div>
  )
}

export function BentoSkeleton({ model, theme, onSelectPlayer, orgTiles = true }: SkeletonProps) {
  const topMax = model.topics[0]?.count || 1
  // The two rivalry teasers. Built from `model.ranked`, which is `BoardPlayer[]`
  // and therefore carries no `spend` at all — so these panels cannot put a cost
  // figure on screen and need no hide-costs branch.
  const teamRivalry = buildRivalry(model.ranked, 'team', model.series, model.contestFloor)
  const officeRivalry = buildRivalry(model.ranked, 'office', model.series, model.contestFloor)
  // The development league, on the front page at last, with the ladder's exact
  // framing. Same percentile cut LadderSkeleton uses, so the two views agree
  // about who is "getting started".
  const devFrom = Math.max(Math.max(1, Math.ceil(model.ranked.length * 0.25)), Math.ceil(model.ranked.length * 0.6))
  const development = model.ranked.slice(devFrom)
  const longestRun = model.ranked.reduce((m, p) => Math.max(m, p.streak), 0)

  return (
    <div className="bento" data-rank-list="bento">
      {/* ---- ZONE 1: one full-width strip. The two lonely KPI cards and the
           gradient highlight used to sit in the right columns and leave the
           board towering over them; as a strip they fill the top instead of
           failing to fill the side. ---- */}
      {/* NOT RENDERED, rather than hidden, when the page above has already
          drawn an org row -- see `SkeletonProps.orgTiles`. The `hidden`
          attribute would not have worked: `.arcade .bstrip` sets
          `display: grid`, and a class rule beats the user agent's
          `[hidden] { display: none }`, so the strip would have stayed on
          screen while disappearing from the accessibility tree -- the worst
          of both. */}
      {!orgTiles ? null : (
      <div className="bstrip">
        <div className="bstat">
          <span className="sl">Active staff</span>
          <span className="sv">{model.org.players}</span>
          <span className="ss">signed in and using CHU-GPT this window</span>
        </div>
        <div className="bstat">
          <span className="sl">Live streaks</span>
          <span className="sv">{model.org.liveStreaks}</span>
          <span className="ss">{model.org.newThisWindow} ran their first chat recently</span>
        </div>
        <div className="bstat">
          <span className="sl">Tokens this window</span>
          <span className="sv">{model.org.totalTokensText}</span>
          <span className="ss">longest run {longestRun} days</span>
        </div>
        <div className="bstat">
          <span className="sl">Cost per person</span>
          {/* A `Figure`. There is no number on the model to print here — see
              `OrgTotals.costPerPerson`. */}
          <span className="sv" data-withheld={model.org.costPerPerson.withheld ? '1' : '0'}>
            {model.org.costPerPerson.text}
          </span>
          <span className="ss">platform spend ÷ {model.org.players} active</span>
        </div>
        {model.you ? (
          <div className="bstat accent myou">
            <span className="sl">Your position</span>
            <span className="sv">#{model.you.player.rank}</span>
            <span className="ss">
              {model.you.player.tokensText} tokens
              {model.you.player.streak >= 3 ? ` · ${model.you.player.streak}-day streak` : ''}
            </span>
          </div>
        ) : (
          <div className="bstat accent">
            <span className="sl">Toward {model.org.nextMilestoneText}</span>
            <span className="sv">
              {Math.round((model.org.totalTokens / model.org.nextMilestone) * 100)}%
            </span>
            <span className="ss">of the next milestone, together</span>
          </div>
        )}
      </div>
      )}

      {/* ---- ZONE 2: board left, three panels of real weight right. ---- */}
      <div className="bcard blead">
        <div className="k">
          <span>Leaderboard · {model.mode === 'spend' ? 'spend' : 'tokens'}</span>
          <span className="more">{model.org.players} people</span>
        </div>
        <SurfaceSplitNote shown={model.surfaceSplit} />
        <div className="blist">
          {model.ranked.map((p) => (
            <BentoBoardRow key={p.id} p={p} model={model} onSelectPlayer={onSelectPlayer} />
          ))}
        </div>
      </div>

      {/* Development league. Front page, second-largest panel, and the ladder's
          framing word for word. Names are abbreviated to a first name and an
          initial — it keeps a long chip row readable, and it keeps the least
          active people from being the most legible thing in the panel. */}
      <div className="bcard bdev">
        <div className="k">
          <span>{theme.rankTitles.base}</span>
        </div>
        {development.length === 0 ? (
          <p className="hint">Everyone is in a division this window.</p>
        ) : (
          <>
            <p className="devnote">
              <b>{development.length} building momentum.</b> Everyone who logs a chat moves up — no
              bottom call-out.
            </p>
            <div className="devgrid">
              {development.map((p) => (
                <button
                  type="button"
                  className={`devchip${p.you ? ' you' : ''}`}
                  key={p.id}
                  onClick={() => onSelectPlayer(p.id)}
                  title={p.name}
                >
                  <PixelAvatar name={p.name} size={20} photoId={p.id} window={model.window} />
                  <span className="cname">{abbreviate(p.name)}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <RivalryTeaser rivalry={teamRivalry} title="Team wars" />
      <RivalryTeaser rivalry={officeRivalry} title="Office of origin" />

      <div className="bcard btax">
        <div className="k">
          <span>What we use it for</span>
        </div>
        {model.topics.length === 0 ? (
          <p className="hint">No topic data in this window.</p>
        ) : (
          model.topics.slice(0, 5).map((t) => (
            <div className="trow" key={t.topic}>
              <span className="tt">{t.topic}</span>
              <span className="tbar">
                <i style={{ width: `${(t.count / topMax) * 100}%` }} />
              </span>
              <b>{t.count.toLocaleString()}</b>
            </div>
          ))
        )}
      </div>

      {/* ---- ZONE 3: two usage lists side by side. This row is full width
           because it holds TWO lists; a single one would have gone in the
           right stack rather than earning a row of its own. ---- */}
      {/* MCP usage is REAL as of 31 Aug 2026 — it comes from the nightly
          warehouse, which reads the ASGI emitter and the message tool parts.
          The card still appears only when there is something in it, because a
          permanently empty panel on an all-staff board reads as broken; the
          difference is that "empty" now means a quiet window rather than an
          unmeasured platform. The full picture, including denials, latency and
          the calling plane, is in the Tools view. */}
      {model.mcp.servers.length > 0 ? (
        <UsageCard
          className="busage"
          title="Tools in play"
          rows={model.mcp.servers.slice(0, 8).map((m) => ({
            name: m.server,
            conversations: m.conversations,
            people: m.people,
          }))}
          empty=""
          note="Conversations counted at SERVER grain, which is the only grain at which they are correct. A tool used twice may have saved more time than one used two hundred times."
          colors={theme.chartColors}
        />
      ) : null}
      {model.usage.agents.length > 0 ? (
        <UsageCard
          className={model.mcp.servers.length > 0 ? 'busage' : 'busage bwide'}
          title="Agents by use"
          rows={model.usage.agents.map((a) => ({ name: a.agent, conversations: a.conversations, people: a.people }))}
          empty=""
          note="Reach is the second column: an agent shared with three people cannot out-rank one shared with three hundred."
          colors={theme.chartColors}
        />
      ) : null}
    </div>
  )
}

/** "maroun maroun" -> "maroun m." Keeps a long chip row readable, and keeps a
 *  full name out of a panel about people who are just getting going. */
function abbreviate(name: string): string {
  const parts = name.trim().split(/\s+/)
  if (parts.length < 2) return name
  return `${parts[0]} ${parts[parts.length - 1]![0]!.toUpperCase()}.`
}

/**
 * The compact two-sided teaser that opens the full rivalry view.
 *
 * Every rule the full view follows applies here too: per active person, both
 * sides at full saturation whatever the score, the gap phrased as "N back"
 * rather than a negative, and no individual named anywhere.
 */
function RivalryTeaser({ rivalry, title }: { rivalry: RivalryModel; title: string }) {
  const f = rivalry.fixture
  return (
    <div className="bcard bvs">
      <div className="k">
        <span>{title}</span>
        {f ? <span className="more">{f.stored ? `Series ${seriesLine(f)}` : 'Recent form'}</span> : null}
      </div>
      {!f ? (
        <p className="hint">Only one group on the board this window — there is nobody to play.</p>
      ) : (
        <div className="vsmini">
          <div className="vsnames">
            <span className="sidea">{f.a.name}</span>
            <span className="sideb">{f.b.name}</span>
          </div>
          <div className="vsrow">
            <span className="vsbar l">
              <i style={{ width: barPct(f.a.score, f.b.score) }} />
            </span>
            <span className="vsmid">
              {f.a.scoreText} / {f.b.scoreText}
            </span>
            <span className="vsbar r">
              <i style={{ width: barPct(f.b.score, f.a.score) }} />
            </span>
          </div>
          <p className="vsnote">
            Per active person.{' '}
            {f.leader === null
              ? 'Level this window.'
              : `${f.leader === 'a' ? f.b.name : f.a.name} is ${f.gapText}.`}
            {f.holder ? ` ${f.holder} hold the shield.` : ''}
            {/* Said out loud rather than hidden. A fixture below the floor is
                still a real comparison; it just cannot move a shield, because
                a side that could not be fielded did not lose. */}
            {f.countsForShield
              ? ''
              : ` Needs ${rivalry.contestFloor} active a side to move the shield.`}
          </p>
        </div>
      )}
    </div>
  )
}

/** Both bars are drawn relative to the LEADER, so the trailing side always has
 *  a visible bar rather than a stub. A side that is behind should still look
 *  like it turned up. */
function barPct(mine: number, theirs: number): string {
  const max = Math.max(mine, theirs, 1)
  return `${Math.max(6, Math.round((mine / max) * 100))}%`
}

function seriesLine(f: RivalryFixture): string {
  let a = 0
  let b = 0
  for (const p of f.pips) {
    if (p === 'a') a += 1
    else if (p === 'b') b += 1
  }
  return `${a}–${b}`
}

/** One usage list. Shared by the front-page strip and the full usage view so
 *  the two can never disagree about what "conversations" and "people" mean. */
export function UsageCard({
  title,
  rows,
  empty,
  note,
  colors,
  className,
}: {
  title: string
  rows: Array<{ name: string; conversations: number; people: number }>
  empty: string
  note: string
  colors: string[]
  className?: string
}) {
  const max = rows[0]?.conversations || 1
  return (
    <div className={`bcard ${className ?? ''}`}>
      <div className="k">
        <span>{title}</span>
      </div>
      {rows.length === 0 ? (
        <p className="hint">{empty}</p>
      ) : (
        <>
          {rows.map((r, i) => (
            <div className="urow" key={r.name}>
              <span className="udot" style={{ background: colors[i % colors.length] }} />
              <span className="uname">{r.name}</span>
              <span className="utrack">
                <i
                  style={{
                    width: `${Math.max(4, Math.round((r.conversations / max) * 100))}%`,
                    background: colors[i % colors.length],
                  }}
                />
              </span>
              <span className="uval">
                {r.conversations.toLocaleString()}
                <em>{r.people} {r.people === 1 ? 'person' : 'people'}</em>
              </span>
            </div>
          ))}
          <p className="unote">{note}</p>
        </>
      )}
    </div>
  )
}

/**
 * `raid` — the org total as a shared health bar.
 *
 * The only skeleton that inverts what the board means: every bar reads as a
 * contribution toward a common goal rather than a position above someone else.
 * For the large share of staff who will never be near the top ten, it is the
 * one framing in the set that gives them a reason to look.
 *
 * The HP target is `model.org.nextMilestone`, derived at the seam from the org
 * token total — deliberately, so that no goal field has to be added to the
 * shared payload, and so the boss re-targets on its own as adoption grows.
 */
export function RaidSkeleton({ model, onSelectPlayer }: SkeletonProps) {
  const done = Math.min(1, model.org.totalTokens / model.org.nextMilestone)
  const remainingPct = Math.round((1 - done) * 100)
  const lead = model.ranked[0]?.tokens || 1
  return (
    <div className="raid" data-rank-list="raid">
      <div className="boss">
        <div className="bface" aria-hidden="true">
          👾
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="bname">The Backlog Behemoth</div>
          <div className="k">
            Falls when CHU logs {model.org.nextMilestoneText} tokens of real work
          </div>
          <div
            className="hp"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={remainingPct}
            aria-label="Boss health remaining"
          >
            <i style={{ width: `${done * 100}%` }} />
            <span className="hplab">
              {model.org.totalTokensText} / {model.org.nextMilestoneText} · {remainingPct}% HP remaining
            </span>
          </div>
        </div>
      </div>

      <div className="rbody">
        <div className="rcard">
          <div className="k">Top damage · this window</div>
          {model.ranked.map((p) => (
            <div
              className={`rrow clickable${p.you ? ' you' : ''}`}
              key={p.id}
              role="button"
              tabIndex={0}
              onClick={() => onSelectPlayer(p.id)}
              onKeyDown={activate(() => onSelectPlayer(p.id))}
            >
              <span className="rk">
                {p.rank}
                <Mover mover={p.mover} />
              </span>
              <PixelAvatar name={p.name} size={24} />
              <span className="nm">
                {p.name}
                {p.you ? ' · YOU' : ''}
              </span>
              <span className="dmg">
                <i style={{ width: `${(p.tokens / lead) * 100}%` }} />
              </span>
              <b data-withheld={p.score.withheld ? '1' : '0'}>{p.score.text}</b>
            </div>
          ))}
        </div>
        <div className="rcard">
          <div className="k">Party contribution by team</div>
          {model.teams.map((t) => (
            <div className="tg" key={t.dept}>
              <div className="tgl">
                <span>{t.dept}</span>
                <b>{t.text}</b>
              </div>
              <span className="dmg">
                <i style={{ width: `${model.teamMax > 0 ? (t.amt / model.teamMax) * 100 : 0}%` }} />
              </span>
            </div>
          ))}
          <div className="rnote">
            🔥 <b>{model.org.liveStreaks}</b> in the party have a live streak. Everyone who joins keeps theirs alive.
          </div>
        </div>
      </div>
    </div>
  )
}

/** Splits a score into per-character flap tiles. */
function flaps(text: string, accent = false) {
  return [...text].map((ch, i) => (
    <span className={`fl${accent ? ' acc' : ''}`} key={`${i}-${ch}`} aria-hidden="true">
      {ch}
    </span>
  ))
}

/**
 * `flaps` — the departures board, and the reception screen.
 *
 * Scores decompose into per-character mechanical tiles. The status column
 * carries TENURE and STREAK, never a rank move: NEW ARRIVAL is
 * `tenureDays <= 7` and ON A RUN is `streak >= 3` — the two facts the board
 * already exposes as the ROOKIE and 🔥 badges, so nothing new is revealed and
 * nothing spend-derived is used.
 *
 * The tiles are `aria-hidden` and each row carries an accessible label, since
 * a screen reader should hear "rank 1, kofi adeyemi, 38.6M" rather than
 * fourteen separate characters.
 */
export function FlapsSkeleton({ model, onSelectPlayer, orgTiles = true }: SkeletonProps) {
  return (
    <div className="flapboard" data-rank-list="flaps">
      <div className="flapmarquee" aria-hidden="true">
        {flaps('CHU-GPT')}
        <span className="flgap" />
        {flaps('ARCADE', true)}
      </div>
      <div className="brows">
        {model.ranked.map((p) => {
          const status = p.isNew ? 'NEW ARRIVAL' : p.streak >= 3 ? `ON A RUN ${p.streak}D` : 'SCHEDULED'
          const tone = p.isNew ? 'new' : p.streak >= 3 ? 'hot' : ''
          return (
            <div
              className={`brow clickable${p.you ? ' you' : ''}${p.isNew ? ' hi' : ''}`}
              key={p.id}
              role="button"
              tabIndex={0}
              aria-label={`Rank ${p.rank}, ${p.name}, ${p.department}, ${p.score.text}, ${status}`}
              onClick={() => onSelectPlayer(p.id)}
              onKeyDown={activate(() => onSelectPlayer(p.id))}
            >
              <span className="flaprank">
                {flaps(String(p.rank).padStart(2, '0'), p.rank <= 3)}
                <Mover mover={p.mover} />
              </span>
              <PixelAvatar name={p.name} size={26} />
              <div style={{ minWidth: 0 }}>
                <div className="nm">{p.name}</div>
                <div className="sub">
                  {p.department} · {p.cls.name}
                </div>
              </div>
              <span className="flapscore" data-withheld={p.score.withheld ? '1' : '0'}>
                {flaps(p.score.text)}
              </span>
              <span className={`status ${tone}`}>{status}</span>
            </div>
          )
        })}
      </div>
      <div className="flapbottom">
        <div className="mini">
          <div className="k">Board totals · teams</div>
          {model.teams.slice(0, 3).map((t) => (
            <div className="fr" key={t.dept}>
              <span>{t.dept}</span>
              <b>{t.text}</b>
            </div>
          ))}
        </div>
        <div className="mini">
          <div className="k">Now boarding · topics</div>
          {model.topics.slice(0, 3).map((t) => (
            <div className="fr" key={t.topic}>
              <span>{t.topic}</span>
              <b>{t.count.toLocaleString()}</b>
            </div>
          ))}
        </div>
        {!orgTiles ? null : (
        <div className="mini">
          <div className="k">On the board</div>
          <div className="fr">
            <span>Active staff</span>
            <b>{model.org.players}</b>
          </div>
          <div className="fr">
            <span>New this week</span>
            <b>{model.org.newThisWindow}</b>
          </div>
          <div className="fr">
            <span>Streaks live</span>
            <b>{model.org.liveStreaks}</b>
          </div>
        </div>
        )}
      </div>
    </div>
  )
}

/**
 * `motd` — the terminal scrollback.
 *
 * A genuinely different DOM for identical data: no podium, no cards, no rail,
 * no grid. Prompt line, a real `<table>` (kept semantic rather than
 * pre-formatted text so it is navigable), an ASCII histogram whose bars are
 * `aria-hidden` because the count beside them carries the value, then the
 * message of the day.
 */
export function MotdSkeleton({ model, onSelectPlayer }: SkeletonProps) {
  const topMax = model.topics[0]?.count || 1
  const shown = model.ranked.slice(0, 12)
  return (
    <div className="motd" data-rank-list="motd">
      <div className="mline">
        <span className="mpr">chu@arcade</span>:~$ arcade --top --window={model.window}
      </div>
      <div className="mline mdim">CHU-GPT ARCADE — titles only, no spend, no message content</div>
      <div className="mrule" />
      <table className="mtable">
        <thead>
          <tr className="mdim">
            <th>RANK</th>
            <th>PLAYER</th>
            <th>TEAM</th>
            <th>CLASS</th>
            <th className="mr">{model.mode === 'spend' ? 'SPEND' : 'TOKENS'}</th>
            <th className="mr">STREAK</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((p) => (
            <tr
              key={p.id}
              className={`mrow${p.you ? ' myou' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => onSelectPlayer(p.id)}
              onKeyDown={activate(() => onSelectPlayer(p.id))}
            >
              <td className={p.rank <= 3 ? 'mwarn' : ''}>
                [{p.rank}]
                <Mover mover={p.mover} />
              </td>
              <td className={p.you ? 'mhl' : ''}>
                {p.name}
                {p.you ? ' *' : ''}
              </td>
              <td className="mdim">{p.department}</td>
              <td>{p.cls.name}</td>
              <td className="mr" data-withheld={p.score.withheld ? '1' : '0'}>
                {p.score.text}
              </td>
              <td className="mr">{p.streak}d</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mline mdim">
        {model.you ? '* that’s you · ' : ''}
        {model.org.players} players total · showing {shown.length}
      </div>
      <div className="mrule" />
      <div className="mline">
        <span className="mpr">chu@arcade</span>:~$ arcade --topics
      </div>
      {model.topics.length === 0 ? (
        <div className="mline mdim">no topic data in this window</div>
      ) : (
        model.topics.slice(0, 6).map((t, i) => (
          <div className="mline mhist" key={t.topic}>
            <span className="mtopic">{t.topic}</span>
            <span className={i === 0 ? 'mbar mhl' : 'mbar'} aria-hidden="true">
              {'█'.repeat(Math.max(1, Math.round((t.count / topMax) * 20)))}
            </span>
            <span className="mcount">{t.count.toLocaleString()}</span>
          </div>
        ))
      )}
      <div className="mrule" />
      <div className="mline">
        <span className="mpr">chu@arcade</span>:~$ arcade --motd
      </div>
      <div className="mline mwarn">
        &gt; {model.org.liveStreaks} live streaks across CHU. {model.org.newThisWindow} ran their first chat this week.
      </div>
      <div className="mline">
        <span className="mpr">chu@arcade</span>:~$ <span className="mcur" aria-hidden="true" />
      </div>
    </div>
  )
}

/** The skeleton registry. A theme names one of these; the page looks it up.
 *  Exhaustively keyed on `ArcadeLayoutId`, so adding a layout id without a
 *  renderer is a type error rather than a silent fallback to the table — which
 *  is the failure the structural test is there to catch if it ever happens at
 *  runtime. */
export const LAYOUTS: Record<ArcadeLayoutId, (p: SkeletonProps) => React.ReactElement> = {
  board: BoardSkeleton,
  bento: BentoSkeleton,
  ladder: LadderSkeleton,
  raid: RaidSkeleton,
  flaps: FlapsSkeleton,
  lanes: LanesSkeleton,
  motd: MotdSkeleton,
}
